import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorNordicGrey2, ColorNordicGrey3, Spacing8, Spacing12 } from 'autogen/design-tokens/tokens';
import type { ProductReviewModel } from 'autogen/swagger/all';
import { useDeleteReviewMutation } from 'autogen/swagger/all/hooks';
import { DeleteProductReview, ReviewItemSeeMore } from 'autogen/translation-keys/trans-website-productreview';
import { ShowOriginalLanguage, ShowTranslated } from 'autogen/translation-keys/trans-website-productreview-list';
import {
    ConfirmButton,
    Heading,
    Message
} from 'autogen/translation-keys/trans-website-productreview-myprofile-deletereviewconfirmation';
import { Cancel } from 'autogen/translation-keys/trans-website-shared';
import Card from 'components/generic/card';
import InlineButton from 'components/generic/InlineButton';
import RatingStars from 'components/generic/ratingStars';
import { Typography } from 'components/generic/typography';
import { ReadMoreMaxCharacters } from 'components/react-readmore';
import LikeButtons from '../likeButtons';
import UserInformation from './components/UserInformation';
import Variant from './components/Variant';

const StyledCardFooter = styled(Card.Footer)`
    background-color: ${ColorNordicGrey2};
`;

const StyledUserInformation = styled(UserInformation)`
    padding-bottom: ${Spacing12};
    margin-bottom: ${Spacing12};
    border-bottom: 1px solid ${ColorNordicGrey3};
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${Spacing8};
`;

const Headline = styled(Typography.Body)`
    display: block;
    margin-bottom: 0;
`;

const DeleteButton = styled(InlineButton)`
    margin-left: auto;
`;

export interface IReviewProps {
    model: ProductReviewModel;
}

export default function ReviewCard(props: IReviewProps) {
    const { model } = props;
    const {
        headline,
        note,
        rating,
        allowLike,
        reviewId,
        allowDelete,
        translatedHeadline,
        translatedNote,
        isVariantReview,
        variant
    } = model;
    const [showReview, setShowReview] = useState(true);
    const hasTranslations = !!translatedHeadline;
    const [showTranslation, setShowTranslation] = useState(hasTranslations);
    const { t } = useTranslation();
    const mutation = useDeleteReviewMutation();

    const deleteReview = async () => {
        if (!allowDelete) return;

        const onConfirm = async () => {
            setShowReview(false);
            try {
                await mutation.mutateAsync({ params: { productReviewId: reviewId } });
            } catch {
                window.location.reload();
            }
        };
        const { default: confirmationModal } = await import(
            /* webpackChunkName: "confirmationModal" */ 'modules/modal/confirmationModal'
        );

        const modal = confirmationModal();
        modal({
            open: true,
            header: t(Heading),
            content: <div className="mt-4 mb-5 text-center">{t(Message)}</div>,
            confirmText: t(ConfirmButton),
            denyText: t(Cancel),
            onConfirm
        });
    };

    if (!showReview) return null;

    return (
        <Card>
            <Card.Content>
                <StyledUserInformation model={model} />
                <Row>
                    <RatingStars size="M" rating={rating} />
                    {hasTranslations && (
                        <InlineButton
                            onClick={() => {
                                setShowTranslation(!showTranslation);
                            }}
                        >
                            {showTranslation ? t(ShowOriginalLanguage) : t(ShowTranslated)}
                        </InlineButton>
                    )}
                </Row>
                {isVariantReview && variant && <Variant {...variant} />}
                <div>
                    <Headline forwardedAs="span" size="M" strong>
                        {showTranslation && translatedHeadline ? translatedHeadline : headline}
                    </Headline>

                    <ReadMoreMaxCharacters buttonText={t(ReviewItemSeeMore)}>
                        {showTranslation && translatedNote ? translatedNote : note}
                    </ReadMoreMaxCharacters>
                </div>
            </Card.Content>
            {(allowLike || allowDelete) && (
                <StyledCardFooter>
                    {allowLike && !allowDelete && <LikeButtons model={model} />}
                    {allowDelete && <DeleteButton onClick={deleteReview}>{t(DeleteProductReview)}</DeleteButton>}
                </StyledCardFooter>
            )}
        </Card>
    );
}
